import React from 'react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function PrivacyPolicyPage() {
  const lastUpdated = 'April 2, 2026';
  return <div className="bg-viridia-black min-h-screen pt-32 pb-24" data-id="element-868">
      <SEOHead title="Privacy Policy | Viridia Analytics" description="Privacy Policy for Viridia Analytics. Learn how we collect, use, and protect your personal data." canonical="/privacy" data-id="element-869" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-870">
        <ScrollReveal data-id="element-871">
          <div className="text-center mb-16" data-id="element-872">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-id="element-873">
              Privacy Policy
            </h1>
            <p className="text-gray-400" data-id="element-874">Last Updated: {lastUpdated}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} data-id="element-875">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 prose prose-invert max-w-none" data-id="element-876">
            <div className="space-y-8 text-gray-300 leading-relaxed" data-id="element-877">
              <section data-id="element-878">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-879">
                  1. Introduction
                </h2>
                <p data-id="element-880">
                  Viridia Analytics ("we," "our," or "us") respects your privacy
                  and is committed to protecting your personal data. This
                  Privacy Policy explains how we collect, use, disclose, and
                  safeguard your information when you visit our website or use
                  our marketing and automation services.
                </p>
              </section>

              <section data-id="element-881">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-882">
                  2. Information We Collect
                </h2>
                <p className="mb-2" data-id="element-883">
                  We may collect the following types of information:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-884">
                  <li data-id="element-885">
                    <strong data-id="element-886">Personal Identification Information:</strong> Name,
                    email address, phone number (including for SMS
                    communications), and company details when you fill out a
                    contact form, book a consultation, or opt in to messaging.
                  </li>
                  <li data-id="element-887">
                    <strong data-id="element-888">Business Information:</strong> Information about
                    your business, marketing goals, current advertising
                    platforms, and CRM systems.
                  </li>
                  <li data-id="element-889">
                    <strong data-id="element-890">Usage Data:</strong> Information about how you
                    interact with our website, including IP addresses, browser
                    types, pages visited, and time spent on pages.
                  </li>
                </ul>
              </section>

              <section data-id="element-891">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-892">
                  3. How We Use Your Information
                </h2>
                <p className="mb-2" data-id="element-893">
                  We use the collected information for various purposes,
                  including:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-894">
                  <li data-id="element-895">To provide, operate, and maintain our services.</li>
                  <li data-id="element-896">
                    To communicate with you, including responding to inquiries
                    and providing customer support.
                  </li>
                  <li data-id="element-897">
                    To send administrative information, such as updates to our
                    terms or policies.
                  </li>
                  <li data-id="element-898">
                    To analyze usage patterns and improve our website and
                    service offerings.
                  </li>
                  <li data-id="element-899">
                    To manage and optimize advertising campaigns on your behalf.
                  </li>
                </ul>
              </section>

              <section data-id="element-900">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-901">
                  4. SMS/Text Messaging Privacy
                </h2>
                <p className="mb-2" data-id="element-902">
                  If you opt in to receive text messages from us, the following
                  applies:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-903">
                  <li data-id="element-904">
                    We collect phone numbers when users submit contact forms,
                    book consultations, or opt in to receive text messages.
                  </li>
                  <li data-id="element-905">
                    Phone numbers may be used to send appointment confirmations,
                    follow-up messages, service updates, and promotional offers
                    via SMS.
                  </li>
                  <li data-id="element-906">
                    By providing your phone number and opting in, you consent to
                    receive automated text messages from Viridia Analytics.
                  </li>
                  <li data-id="element-907">
                    Message and data rates may apply. Message frequency varies.
                  </li>
                  <li data-id="element-908">
                    You can opt out at any time by replying{' '}
                    <strong data-id="element-909">STOP</strong> to any message.
                  </li>
                  <li data-id="element-910">
                    Reply <strong data-id="element-911">HELP</strong> for assistance.
                  </li>
                  <li data-id="element-912">
                    <strong data-id="element-913">
                      SMS consent and phone numbers are NOT shared with third
                      parties or affiliates for marketing purposes.
                    </strong>
                  </li>
                  <li data-id="element-914">
                    We use third-party platforms (e.g.,
                    GoHighLevel/LeadConnector) solely to facilitate our own SMS
                    communications with you.
                  </li>
                </ul>
              </section>

              <section data-id="element-915">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-916">
                  5. Cookies and Tracking Technologies
                </h2>
                <p data-id="element-917">
                  We use cookies and similar tracking technologies to track
                  activity on our website and hold certain information. Cookies
                  are files with a small amount of data which may include an
                  anonymous unique identifier. You can instruct your browser to
                  refuse all cookies or to indicate when a cookie is being sent.
                </p>
              </section>

              <section data-id="element-918">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-919">
                  6. Third-Party Services
                </h2>
                <p data-id="element-920">
                  In providing our services, we interact with third-party
                  platforms such as Google Ads, Meta (Facebook/Instagram), Yelp,
                  and various CRM providers. We may share necessary information
                  with these platforms solely for the purpose of executing the
                  marketing services you have contracted us to perform. We do
                  not sell your personal data to third parties.
                </p>
              </section>

              <section data-id="element-921">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-922">
                  7. Data Security
                </h2>
                <p data-id="element-923">
                  We implement appropriate technical and organizational security
                  measures designed to protect the security of any personal
                  information we process. However, please note that no
                  electronic transmission over the Internet or information
                  storage technology can be guaranteed to be 100% secure.
                </p>
              </section>

              <section data-id="element-924">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-925">
                  8. Your Rights
                </h2>
                <p data-id="element-926">
                  Depending on your location, you may have certain rights
                  regarding your personal data, including the right to access,
                  correct, or delete the information we hold about you. To
                  exercise these rights, please contact us using the information
                  provided below.
                </p>
              </section>

              <section data-id="element-927">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-928">
                  9. Changes to This Privacy Policy
                </h2>
                <p data-id="element-929">
                  We may update our Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last Updated" date.
                </p>
              </section>

              <section data-id="element-930">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-931">
                  10. Contact Us
                </h2>
                <p className="mb-2" data-id="element-932">
                  If you have any questions about this Privacy Policy, please
                  contact us:
                </p>
                <ul className="list-none space-y-1" data-id="element-933">
                  <li data-id="element-934">
                    <strong data-id="element-935">Company:</strong> Viridia Analytics
                  </li>
                  <li data-id="element-936">
                    <strong data-id="element-937">Location:</strong> Phoenix, AZ
                  </li>
                  <li data-id="element-938">
                    <strong data-id="element-939">Email:</strong>{' '}
                    <a href="mailto:info@viridiaanalytics.com" className="text-viridia hover:underline" data-id="element-940">
                      info@viridiaanalytics.com
                    </a>
                  </li>
                  <li data-id="element-941">
                    <strong data-id="element-942">Phone:</strong>{' '}
                    <a href="tel:6233000708" className="text-viridia hover:underline" data-id="element-943">
                      (623) 300-0708
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}